import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Subfamily } from '../../types';
import { subfamilyService } from '../../services/subfamilyService';

interface SubfamilySelectorProps {
  familyId: string;
  selectedSubfamilyId: string;
  onSubfamilyChange: (subfamilyId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const SelectorContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SelectorInput = styled.input<{ disabled?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #ecf0f1;
  border-radius: 8px;
  font-size: 1rem;
  background-color: ${props => props.disabled ? '#f8f9fa' : 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'text'};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.disabled ? '#ecf0f1' : 'var(--primary-orange)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 0 0 3px rgba(255, 140, 66, 0.1)'};
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
`;

const DropdownItem = styled.div<{ selected?: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  background-color: ${props => props.selected ? '#fff3e0' : 'white'};
  color: ${props => props.selected ? '#e65100' : '#333'};
  font-weight: ${props => props.selected ? '600' : 'normal'};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.selected ? '#fff3e0' : '#f8f9fa'};
    color: ${props => props.selected ? '#e65100' : 'var(--primary-orange)'};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const LoadingMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
`;

const NoResultsMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
`;

const HelpText = styled.small`
  color: #666;
  font-size: 0.8rem;
  margin-top: 4px;
  display: block;
`;

const SubfamilySelector: React.FC<SubfamilySelectorProps> = ({
  familyId,
  selectedSubfamilyId,
  onSubfamilyChange,
  disabled = false,
  placeholder = "Buscar subfamilia..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [subfamilies, setSubfamilies] = useState<Subfamily[]>([]);
  const [filteredSubfamilies, setFilteredSubfamilies] = useState<Subfamily[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSubfamily, setSelectedSubfamily] = useState<Subfamily | null>(null);
  const selectorRef = useRef<HTMLDivElement>(null);

  // Cargar subfamilias cuando cambia la familia
  useEffect(() => {
    if (familyId && !disabled) {
      loadSubfamilies();
    } else {
      setSubfamilies([]);
      setFilteredSubfamilies([]);
      setSelectedSubfamily(null);
      setSearchTerm('');
    }
  }, [familyId, disabled]);

  // Filtrar subfamilias cuando cambia el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      // Sin búsqueda: mostrar solo las primeras 5 subfamilias para evitar lentitud
      setFilteredSubfamilies(subfamilies.slice(0, 5));
    } else {
      // Con búsqueda: mostrar todos los resultados coincidentes
      const filtered = subfamilies.filter(subfamily =>
        subfamily.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSubfamilies(filtered);
    }
  }, [searchTerm, subfamilies]);

  // Actualizar subfamilia seleccionada cuando cambia el ID seleccionado
  useEffect(() => {
    if (selectedSubfamilyId && subfamilies.length > 0) {
      // Convertir ambos a string para comparación consistente
      const subfamily = subfamilies.find(s => String(s.id) === String(selectedSubfamilyId));
      if (subfamily) {
        setSelectedSubfamily(subfamily);
        setSearchTerm(subfamily.name);
        console.log('Subfamilia seleccionada encontrada:', subfamily.name);
      } else {
        console.log('Subfamilia no encontrada para ID:', selectedSubfamilyId, 'en subfamilias:', subfamilies.map(s => s.id));
      }
    } else {
      setSelectedSubfamily(null);
      setSearchTerm('');
    }
  }, [selectedSubfamilyId, subfamilies]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Si no hay subfamilia seleccionada, limpiar el campo
        if (!selectedSubfamily) {
          setSearchTerm('');
        } else {
          setSearchTerm(selectedSubfamily.name);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedSubfamily]);

  const loadSubfamilies = async () => {
    setLoading(true);
    try {
      // Cargar todas las subfamilias de la familia para tener datos completos para búsqueda
      const response = await subfamilyService.getSubfamiliesWithPagination(1, 100, familyId);
      if (response && response.subfamilies) {
        setSubfamilies(response.subfamilies);
        // Inicialmente mostrar solo las primeras 5 subfamilias
        setFilteredSubfamilies(response.subfamilies.slice(0, 5));
      }
    } catch (error) {
      console.error('Error al cargar subfamilias:', error);
      setSubfamilies([]);
      setFilteredSubfamilies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    
    // Si el usuario borra el texto, limpiar la selección
    if (!value.trim()) {
      setSelectedSubfamily(null);
      onSubfamilyChange('');
    }
  };

  const handleSubfamilySelect = (subfamily: Subfamily) => {
    setSelectedSubfamily(subfamily);
    setSearchTerm(subfamily.name);
    setIsOpen(false);
    onSubfamilyChange(subfamily.id);
  };

  const handleInputFocus = () => {
    if (!disabled && familyId) {
      setIsOpen(true);
    }
  };

  const handleClearSelection = () => {
    setSelectedSubfamily(null);
    setSearchTerm('');
    onSubfamilyChange('');
    setIsOpen(false);
  };

  return (
    <SelectorContainer ref={selectorRef}>
      <SelectorInput
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={disabled ? "Primero selecciona una familia" : placeholder}
        disabled={disabled || !familyId}
      />
      
      {!familyId && (
        <HelpText>Primero selecciona una familia</HelpText>
      )}
      
      {isOpen && familyId && !disabled && (
        <DropdownContainer>
          {loading ? (
            <LoadingMessage>Cargando subfamilias...</LoadingMessage>
          ) : (
            <>
              {/* Opción para limpiar selección */}
              <DropdownItem
                onClick={handleClearSelection}
                selected={!selectedSubfamily}
              >
                <em>Sin subfamilia</em>
              </DropdownItem>
              
              {filteredSubfamilies.length > 0 ? (
                filteredSubfamilies.map(subfamily => (
                  <DropdownItem
                    key={subfamily.id}
                    onClick={() => handleSubfamilySelect(subfamily)}
                    selected={selectedSubfamily?.id === subfamily.id}
                  >
                    {subfamily.name}
                    {subfamily.description && (
                      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                        {subfamily.description}
                      </div>
                    )}
                  </DropdownItem>
                ))
              ) : (
                <NoResultsMessage>
                  {searchTerm ? `No se encontraron subfamilias que coincidan con "${searchTerm}"` : 'No hay subfamilias disponibles'}
                </NoResultsMessage>
              )}
            </>
          )}
        </DropdownContainer>
      )}
    </SelectorContainer>
  );
};

export default SubfamilySelector;